/**
 * SWISS School Portal - Fleet-wide Auth Logs bridge
 * ===================================================
 * Every portal in the MSS fleet pushes a fire-and-forget event to the
 * IT Admin portal's real-time "Auth Logs" feature on every login
 * success/failure. Most portals do this server-side, holding the
 * shared write key as a backend env var.
 *
 * SWISS can't do that the same way: real credential checks never touch
 * our own backend — Google federation and the native email/password
 * form both run on Cognito's hosted UI domain, and demo sign-in calls
 * Cognito's InitiateAuth directly from the browser. There is no
 * server-side login handler to attach this to for most surfaces.
 *
 * So instead of the browser holding the shared write key (this is a
 * Create React App — every REACT_APP_* env var ships in the public
 * bundle, so the key would be exposed to anyone who opens devtools),
 * this posts to our own admin-lambda at POST /public/auth-log. That
 * Lambda holds AUTH_LOG_WRITE_API_KEY server-side and relays the event
 * to the real fleet endpoint. See recordAuthLogEvent() in
 * infrastructure/admin-lambda/index.js.
 *
 * Successful sign-ins are NOT sent from here — they're pushed from the
 * server-side recordLogin() handler behind POST /admin/login-event,
 * which every real login surface already calls once a token is issued
 * (see recordLogin() in AuthContext.js). This module only carries
 * failures, where by definition there may be no valid session token to
 * call that authenticated endpoint with.
 */

import { config } from '../config/config';

const AUTH_LOG_RELAY_URL = `${config.admin.apiUrl.replace(/\/+$/, '')}/public/auth-log`;

/**
 * Record a failed SWISS login attempt with the fleet-wide Auth Logs
 * feature. Best-effort — never throws, never blocks the caller.
 */
export const recordAuthLogFailure = ({ identifier = null, reason, status = null, path = null } = {}) => {
  try {
    fetch(AUTH_LOG_RELAY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        identifier: identifier || null,
        outcome: 'failure',
        reason: reason || 'Login failed',
        status,
        path,
      }),
      keepalive: true,
      signal: AbortSignal.timeout ? AbortSignal.timeout(3000) : undefined,
    }).catch((e) => console.error('[AuthLogWrite] failed:', e.message));
  } catch (e) {
    console.error('[AuthLogWrite] failed:', e.message);
  }
};
